// Extracted from EnhancedResults.js (structural refactor, July 2026).
// Analysis-result view components — no behavior change.
import React from 'react';
import AugmentedDesignBlocks from '@/components/results/AugmentedDesignBlocks';
import DecisionCardInlineView from '@/components/results/DecisionCardInlineView';
import { MolecularMarkerScanBanner } from '@/components/results/MolecularMarkerScanBanner';
import { PostHocSuiteCard } from '@/components/results/PostHocSuiteCard';
import { Card } from '@/components/ui/card';

export function GenericAnalysisView({ data, sectionKey }) {
  if (!data) return null;
  const skipKeys = new Set(['chart', 'charts', 'classroom_mode', 'interpretation', 'anova_table', 'analysis', 'all_traits_analyzed', 'multi_trait_results']);

  /* Render a single list-of-dicts as a table */
  const renderListTable = (title, items, testIdSuffix) => {
    if (!items || !items.length) return null;
    const cols = Object.keys(items[0]);
    return (
      <div className="overflow-x-auto mt-4" data-testid={`table-${testIdSuffix}`}>
        <h4 className="font-semibold text-[#2C2A28] mb-3 text-sm uppercase tracking-wider">{title}</h4>
        <table className="w-full text-sm border-collapse">
          <thead><tr className="bg-[#2D6A4F] text-white">
            {cols.map(c => <th key={c} className="px-3 py-2 text-center text-xs font-semibold border border-[#1B4332]">{c.replace(/_/g, ' ')}</th>)}
          </tr></thead>
          <tbody>
            {items.map((row, ri) => (
              <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-[#F0FAF0]'}>
                {cols.map(c => {
                  const v = row[c];
                  const isSig = c === 'Sig' || c === 'Significance';
                  return (
                    <td key={c} className="px-3 py-1.5 text-center font-mono text-xs border border-[#E8E6E1]">
                      {isSig ? (
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${v === '**' ? 'bg-emerald-500 text-white' : v === '*' ? 'bg-amber-500 text-white' : 'bg-gray-200 text-gray-600'}`}>{v || 'ns'}</span>
                      ) : typeof v === 'number' ? v.toFixed(4) : typeof v === 'boolean' ? (v ? 'Yes' : 'No') : (v === null || v === undefined) ? '-' : String(v)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  /* Render a flat dict as key-value pairs */
  const renderKVTable = (title, obj, testIdSuffix) => {
    const entries = Object.entries(obj).filter(([, v]) => typeof v !== 'object' || v === null);
    if (!entries.length) return null;
    return (
      <div className="overflow-x-auto mt-4" data-testid={`kv-${testIdSuffix}`}>
        <h4 className="font-semibold text-[#2C2A28] mb-3 text-sm uppercase tracking-wider">{title}</h4>
        <table className="w-full text-sm border-collapse">
          <thead><tr className="bg-[#F5F4F2]">
            <th className="border border-[#E8E6E1] px-4 py-2 text-left font-semibold">Parameter</th>
            <th className="border border-[#E8E6E1] px-4 py-2 text-right font-semibold">Value</th>
          </tr></thead>
          <tbody>
            {entries.map(([k, v]) => (
              <tr key={k} className="hover:bg-[#F9F8F6]">
                <td className="border border-[#E8E6E1] px-4 py-2 text-[#2C2A28]">{k.replace(/_/g, ' ')}</td>
                <td className="border border-[#E8E6E1] px-4 py-2 text-right font-mono text-[#5E5A56]">
                  {typeof v === 'number' ? v.toFixed(4) : typeof v === 'boolean' ? (v ? 'Yes' : 'No') : (v === null ? '-' : String(v))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  /* Render a single trait's results */
  const renderTraitResults = (traitName, traitData) => {
    if (!traitData || typeof traitData !== 'object') return null;
    return (
      <div key={traitName} className="mb-6 rounded-xl border-l-4 border-[#2D6A4F] bg-white p-4" style={{ borderColor: 'var(--green-500)' }} data-testid={`trait-${traitName}`}>
        <h3 className="font-semibold text-base text-[#2C2A28] mb-3">{traitName}</h3>
        {/* ANOVA table from array */}
        {Array.isArray(traitData.anova) && renderListTable('ANOVA', traitData.anova, `anova-${traitName}`)}
        {/* Scaling tests */}
        {Array.isArray(traitData.scaling_tests) && renderListTable('Scaling Tests', traitData.scaling_tests, `scaling-${traitName}`)}
        {/* Generation means */}
        {Array.isArray(traitData.generation_means) && renderListTable('Generation Means', traitData.generation_means, `gen-means-${traitName}`)}
        {/* Genotype ranking */}
        {Array.isArray(traitData.genotype_ranking) && renderListTable('Genotype Ranking', traitData.genotype_ranking, `geno-rank-${traitName}`)}
        {/* Treatment means as array */}
        {Array.isArray(traitData.treatment_means) && renderListTable('Treatment Means', traitData.treatment_means, `trt-means-${traitName}`)}
        {/* Flat key-value sections */}
        {traitData.variance_components && typeof traitData.variance_components === 'object' && renderKVTable('Variance Components', traitData.variance_components, `vc-${traitName}`)}
        {traitData.icc && typeof traitData.icc === 'object' && renderKVTable('ICC (Intraclass Correlation)', traitData.icc, `icc-${traitName}`)}
        {traitData.epistasis_test && typeof traitData.epistasis_test === 'object' && renderKVTable('Epistasis Test', traitData.epistasis_test, `epi-${traitName}`)}
        {traitData.genetic_components && typeof traitData.genetic_components === 'object' && renderKVTable('Genetic Components', traitData.genetic_components, `gc-${traitName}`)}
        {traitData.six_parameter_model && typeof traitData.six_parameter_model === 'object' && renderKVTable('Six-Parameter Model (Hayman 1958)', traitData.six_parameter_model, `6p-${traitName}`)}
        {traitData.joint_scaling && typeof traitData.joint_scaling === 'object' && renderKVTable('Joint Scaling Test', traitData.joint_scaling, `jst-${traitName}`)}
        {traitData.design_params && typeof traitData.design_params === 'object' && renderKVTable('Design Parameters', traitData.design_params, `dp-${traitName}`)}
        {/* Treatment means as dict */}
        {traitData.treatment_means && !Array.isArray(traitData.treatment_means) && typeof traitData.treatment_means === 'object' && renderKVTable('Treatment Means', traitData.treatment_means, `trt-kv-${traitName}`)}
        {traitData.male_means && typeof traitData.male_means === 'object' && renderKVTable('Male Means', traitData.male_means, `male-${traitName}`)}
        {traitData.adjusted_means && typeof traitData.adjusted_means === 'object' && renderKVTable('Adjusted Means', traitData.adjusted_means, `adj-${traitName}`)}
        {traitData.h_means && typeof traitData.h_means === 'object' && renderKVTable('H-Strip Means', traitData.h_means, `hmeans-${traitName}`)}
        {traitData.v_means && typeof traitData.v_means === 'object' && renderKVTable('V-Strip Means', traitData.v_means, `vmeans-${traitName}`)}
        {/* Factor means with grouping letters */}
        {['factor_a', 'factor_b', 'factor_c', 'factor_d'].map(fk => {
          const meansKey = `${fk}_means`;
          const groupsKey = `${fk}_groups`;
          const fm = traitData[meansKey];
          const fg = traitData[groupsKey] || {};
          if (!fm || typeof fm !== 'object') return null;
          const cdKey = fk.replace('factor_', 'cd_');
          const cdVal = traitData[cdKey];
          const entries = Object.entries(fm).sort((a, b) => b[1] - a[1]);
          return (
            <div key={fk} className="overflow-x-auto mt-4" data-testid={`means-${fk}-${traitName}`}>
              <h4 className="font-semibold text-[#2C2A28] mb-3 text-sm uppercase tracking-wider">{fk.replace('factor_', 'Factor ').toUpperCase()} Marginal Means</h4>
              <table className="w-full text-sm border-collapse">
                <thead><tr className="bg-[#2D6A4F] text-white">
                  <th className="px-3 py-2 text-center text-xs font-semibold border border-[#1B4332]">Level</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold border border-[#1B4332]">Mean</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold border border-[#1B4332]">Grouping</th>
                </tr></thead>
                <tbody>
                  {entries.map(([lvl, val], ri) => (
                    <tr key={lvl} className={ri % 2 === 0 ? 'bg-white' : 'bg-[#F0FAF0]'}>
                      <td className="px-3 py-1.5 text-center font-semibold text-xs border border-[#E8E6E1]">{lvl}</td>
                      <td className="px-3 py-1.5 text-center font-mono text-xs border border-[#E8E6E1]">{typeof val === 'number' ? val.toFixed(4) : val}</td>
                      <td className="px-3 py-1.5 text-center font-bold text-xs border border-[#E8E6E1] text-emerald-700">{fg[lvl] || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {typeof cdVal === 'number' && cdVal > 0 && <p className="text-xs italic text-gray-500 mt-1">CD(5%) = {cdVal.toFixed(4)}</p>}
            </div>
          );
        })}
        {/* Significant interaction cell means tables */}
        {traitData.interaction_tables && typeof traitData.interaction_tables === 'object' && Object.entries(traitData.interaction_tables).map(([intKey, intData]) => {
          if (!intData || !intData.table) return null;
          return (
            <div key={intKey} className="overflow-x-auto mt-4" data-testid={`interaction-${intKey}-${traitName}`}>
              <h4 className="font-semibold text-[#2C2A28] mb-3 text-sm uppercase tracking-wider">{intKey.replace(/x/g, ' x ')} Interaction Cell Means (Significant)</h4>
              {renderListTable(`${intKey} Cell Means`, intData.table, `int-${intKey}-${traitName}`)}
              {typeof intData.cd_value === 'number' && <p className="text-xs italic text-gray-500 mt-1">CD(5%) for interaction = {intData.cd_value.toFixed(4)}</p>}
            </div>
          );
        })}
        {/* Scalar values */}
        {typeof traitData.grand_mean === 'number' && <p className="text-sm mt-2"><strong>Grand Mean:</strong> <span className="font-mono">{traitData.grand_mean.toFixed(4)}</span></p>}
        {typeof traitData.cv_percent === 'number' && <p className="text-sm"><strong>CV (%):</strong> <span className="font-mono">{traitData.cv_percent.toFixed(2)}%</span></p>}
        {/* CD values with dynamic labels */}
        {traitData.cd_values && typeof traitData.cd_values === 'object' && (
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid={`cd-values-${traitName}`}>
            {Object.entries(traitData.cd_values).map(([label, val]) => (
              <div key={label} className="bg-[#F0FAF0] rounded-lg px-3 py-2 text-center border border-[#D5ECD5]">
                <div className="text-sm font-bold text-[#2D6A4F] font-mono">{typeof val === 'number' ? val.toFixed(4) : val}</div>
                <div className="text-[10px] text-[#6B8E7B] uppercase font-medium">{label}</div>
              </div>
            ))}
          </div>
        )}
        {/* Post-hoc multiple-comparison suite (LSD / DMRT / Tukey HSD).
            Auto-triggered when ANOVA p < 0.05. */}
        {traitData.posthoc_suite && (
          <PostHocSuiteCard
            suite={traitData.posthoc_suite}
            testIdPrefix={`posthoc-${traitName}`}
          />
        )}
        {typeof traitData.cd === 'number' && <p className="text-sm"><strong>CD (5%):</strong> <span className="font-mono">{traitData.cd.toFixed(4)}</span></p>}
        {typeof traitData.heritability_narrow_sense === 'number' && <p className="text-sm"><strong>h²(ns):</strong> <span className="font-mono">{traitData.heritability_narrow_sense.toFixed(2)}%</span></p>}
        {typeof traitData.degree_of_dominance === 'number' && <p className="text-sm"><strong>Degree of Dominance:</strong> <span className="font-mono">{traitData.degree_of_dominance.toFixed(4)}</span></p>}
        {traitData.dominance_type && <p className="text-sm"><strong>Dominance Type:</strong> {traitData.dominance_type}</p>}
        {traitData.gene_action && <p className="text-sm mt-1 px-3 py-2 rounded-lg" style={{ background: traitData.gene_color === 'green' ? '#ECFDF5' : traitData.gene_color === 'red' ? '#FEF2F2' : '#FFFBEB', border: '1px solid', borderColor: traitData.gene_color === 'green' ? '#A7F3D0' : traitData.gene_color === 'red' ? '#FECACA' : '#FDE68A' }}><strong>Gene Action:</strong> {traitData.gene_action}</p>}
        {traitData.breeding_implication && <p className="text-sm mt-1 italic text-[#5E5A56]">{traitData.breeding_implication}</p>}
        {traitData.practical_recommendation && <p className="text-sm mt-1 italic text-[#5E5A56]">{traitData.practical_recommendation}</p>}
        {traitData.warning && <p className="text-sm mt-1 text-amber-600 font-medium">{traitData.warning}</p>}
        {traitData.efficiency_vs_rbd && <p className="text-sm"><strong>Efficiency vs RBD:</strong> <span className="font-mono">{traitData.efficiency_vs_rbd}%</span></p>}
        {/* Chart */}
        {traitData.chart && <img src={`data:image/png;base64,${traitData.chart}`} alt={`${traitName} Chart`} className="w-full rounded-xl border border-[#E8E6E1] mt-3" />}

        {/* Sub-Batch 3R (Feb 2026): Augmented-design-specific blocks
            (adjusted means, Dunnett, 4 CDs, 4 SEs, block effects,
            variance components, power rating). Only renders if the
            trait dict carries augmented-shaped keys. */}
        <AugmentedDesignBlocks traitData={traitData} traitName={traitName} />

        {/* Sub-Batch 3R (Feb 2026): Universal BLUP + Consensus + ΔG
            "Decision Card Inline View". Mirrors the downloadable
            PDF so screen + paper stay in lockstep. Auto-renders for
            any design wired to the Universal Selection Index
            (Augmented, RBD, Alpha-Lattice, Split-Plot, Strip-Plot). */}
        <DecisionCardInlineView
          blup={traitData.blup_results}
          breedingProgress={traitData.breeding_progress}
          traitName={traitName}
        />
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid={`generic-view-${sectionKey}`}>
      {/* Sub-Batch 3C (Niranjan, Feb 26 2026) — Augmented Design auto-detection
          provenance banner.  Mandatory on every Augmented report (ABD or
          ACRD); renders refusal payloads with a distinct red treatment. */}
      {data.provenance_banner && (
        <div
          data-testid="aug-provenance-banner"
          className={`rounded-2xl p-4 border-2 ${
            data.refuse
              ? 'bg-rose-50 border-rose-300 text-rose-900'
              : data.design_subtype === 'acrd'
                ? 'bg-amber-50 border-amber-300 text-amber-900'
                : 'bg-emerald-50 border-emerald-300 text-emerald-900'
          }`}
        >
          <div className="flex items-start gap-3">
            <span className="font-mono text-xs uppercase tracking-wider px-2 py-1 rounded bg-white/70 border border-current">
              {data.refuse
                ? 'Analysis refused'
                : `Detected: ${data.design_subtype === 'acrd' ? 'ACRD' : 'ABD'}`}
            </span>
            <div className="flex-1">
              <p className="text-sm font-medium leading-relaxed">
                {data.provenance_banner}
              </p>
              {!data.refuse && (
                <p className="text-xs italic mt-2 opacity-80">
                  To override auto-detection, pass{' '}
                  <code className="font-mono">force_design=&quot;abd&quot;</code> or{' '}
                  <code className="font-mono">force_design=&quot;acrd&quot;</code>{' '}
                  when uploading the file.
                </p>
              )}
              {data.refuse && data.reason && (
                <p className="text-sm mt-2 leading-relaxed">
                  <strong>Reason:</strong> {data.reason}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {data.interpretation && (
        <div className="bg-[#EAF0EC] rounded-2xl p-5">
          <p className="text-sm text-[#2C2A28] leading-relaxed whitespace-pre-line">{data.interpretation}</p>
        </div>
      )}

      {/* If data has 'traits' dict — render each trait with its own ANOVA, variance components, etc. */}
      {data.traits && typeof data.traits === 'object' && !Array.isArray(data.traits) && (
        <div>
          {Object.entries(data.traits).map(([traitName, traitData]) => renderTraitResults(traitName, traitData))}
        </div>
      )}

      {/* Legacy ANOVA Table (dict format with source keys) */}
      {data.anova_table && typeof data.anova_table === 'object' && !Array.isArray(data.anova_table) && (
        <div className="overflow-x-auto">
          <h4 className="font-semibold text-[#2C2A28] mb-3 text-sm uppercase tracking-wider">ANOVA Table</h4>
          <table className="w-full text-sm border-collapse" data-testid="generic-anova-table">
            <thead>
              <tr className="bg-[#EAF0EC]">
                {['Source', 'DF', 'SS', 'MS', 'F-value', 'P-value', 'Sig.'].map(h => (
                  <th key={h} className="border border-[#D5D1CC] px-4 py-2.5 text-left font-semibold text-xs uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(data.anova_table).map(([src, d]) => (
                <tr key={src} className="hover:bg-[#F5F4F2]">
                  <td className="border border-[#E8E6E1] px-4 py-2 font-medium text-[#2C2A28]">{src}</td>
                  <td className="border border-[#E8E6E1] px-4 py-2 text-right font-mono text-[#5E5A56]">{d.DF ?? '-'}</td>
                  <td className="border border-[#E8E6E1] px-4 py-2 text-right font-mono text-[#5E5A56]">{typeof d.SS === 'number' ? d.SS.toFixed(4) : '-'}</td>
                  <td className="border border-[#E8E6E1] px-4 py-2 text-right font-mono text-[#5E5A56]">{typeof d.MS === 'number' ? d.MS.toFixed(4) : '-'}</td>
                  <td className="border border-[#E8E6E1] px-4 py-2 text-right font-mono text-[#5E5A56]">{typeof d.F === 'number' ? d.F.toFixed(4) : '-'}</td>
                  <td className="border border-[#E8E6E1] px-4 py-2 text-right font-mono text-[#5E5A56]">{typeof d.P === 'number' ? d.P.toFixed(6) : '-'}</td>
                  <td className="border border-[#E8E6E1] px-4 py-2 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${
                      d.Sig === '**' ? 'bg-emerald-500 text-white' : d.Sig === '*' ? 'bg-amber-500 text-white' : 'bg-gray-200 text-gray-600'
                    }`}>{d.Sig || 'ns'}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Top-level ANOVA array (for analyses that return anova at top level) */}
      {Array.isArray(data.anova) && renderListTable('ANOVA', data.anova, 'top-anova')}

      {/* Top-level list-of-dicts tables */}
      {Array.isArray(data.scaling_tests) && renderListTable('Scaling Tests', data.scaling_tests, 'top-scaling')}
      {Array.isArray(data.generation_means) && renderListTable('Generation Means', data.generation_means, 'top-gen-means')}
      {Array.isArray(data.genotype_ranking) && renderListTable('Genotype Ranking', data.genotype_ranking, 'top-geno-rank')}

      {/* Top-level KV sections */}
      {data.variance_components && !data.traits && renderKVTable('Variance Components', data.variance_components, 'top-vc')}
      {data.genetic_components && !data.traits && renderKVTable('Genetic Components', data.genetic_components, 'top-gc')}
      {data.six_parameter_model && !data.traits && renderKVTable('Six-Parameter Model', data.six_parameter_model, 'top-6p')}
      {data.joint_scaling && !data.traits && renderKVTable('Joint Scaling Test', data.joint_scaling, 'top-jst')}
      {data.male_means && !data.traits && renderKVTable('Male Means', data.male_means, 'top-male')}

      {/* Other generic dict tables (non-traits, non-skip) */}
      {Object.entries(data).map(([key, val]) => {
        if (skipKeys.has(key) || key === 'traits' || typeof val !== 'object' || val === null || Array.isArray(val)) return null;
        // Skip already-rendered top-level sections
        if (['variance_components', 'genetic_components', 'six_parameter_model', 'joint_scaling', 'male_means'].includes(key)) return null;
        const entries = Object.entries(val);
        if (!entries.length) return null;
        const hasPrimitives = entries.some(([, v]) => typeof v !== 'object' || v === null);
        if (!hasPrimitives) return null;
        const tableTitle = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        return renderKVTable(tableTitle, val, `misc-${key}`);
      })}

      {data.chart && <img src={`data:image/png;base64,${data.chart}`} alt="Analysis Chart" className="w-full rounded-2xl border border-[#E8E6E1] mt-4" />}
      {Array.isArray(data.charts) && data.charts.map((c, i) => (
        <img key={i} src={`data:image/png;base64,${c}`} alt={`Chart ${i + 1}`} className="w-full rounded-2xl border border-[#E8E6E1] mt-4" />
      ))}

      {/* Marker-type validation banner (Diversity / Molecular).
          Surfaces detected marker types and the pipeline assumption
          (Pattern 3 + Pattern 10). */}
      {data.marker_type_scan && (
        <MolecularMarkerScanBanner scan={data.marker_type_scan} testId="top-marker-type-scan" />
      )}

      {/* Post-hoc multiple-comparison suite at the top level
          (CRD, single-trait RBD). */}
      {data.posthoc_suite && (
        <PostHocSuiteCard suite={data.posthoc_suite} testIdPrefix="posthoc-top" />
      )}
    </div>
  );
}

/* ============================================================
   MULTIPLE CORRELATION VIEW
============================================================ */
